import { useCallback, useEffect, useMemo, useState } from 'react';

import { Trigger } from '@radix-ui/react-dialog';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import type { LatLng } from 'leaflet';
import {
  Controller,
  type UseFormReturn,
  useForm,
  useFormContext,
} from 'react-hook-form';

import { ReactComponent as SaveIcon } from '@quick/assets/icons/save.svg';
import { useUserDetails } from '@quick/auth';
import { Button } from '@quick/components/Button';
import { WrappedInput } from '@quick/components/Forms';
import { Modal } from '@quick/components/Modal';
import { useToast } from '@quick/components/Toaster';
import type { DetailError } from '@quick/go-api-types/common';
import type { IGeoDirection } from '@quick/go-api-types/geo/direction';
import type { IFavAddressList } from '@quick/go-api-types/users/addresses';

import { useAppTranslation } from '@/app/i18n/client';
import { SelectPoint } from '@/components/address';
import { InlineSpinner } from '@/components/core';
import { useLangProvider } from '@/components/core/AppLayout';
import CitySearch from '@/components/core/CitySearch';
import { standardError } from '@/layers/core/deal/mappers/standardError';
import { useDebounce } from '@/layers/core/deal/services/useDebounce';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { mutate } from '@/layers/core/helpers/mutate';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

import type { IForm } from '../types';

export default function Save({ index }: { index: number }) {
  const [isOpen, set] = useState(false);
  const open = () => set(true);
  const close = () => set(false);

  return (
    <Modal
      events={{ onClose: close }}
      className="z-75"
      contentClassName="bg-background-light dark:bg-background-dark"
      isOpen={isOpen}
      trigger={
        <Trigger
          className="group/rm aspect-square text-blue-600 disabled:text-blue-300"
          onClick={open}
        >
          <SaveIcon
            className="size-8 fill-transparent group-hover/rm:fill-blue-300
              group-focus/rm:fill-blue-300"
          />
        </Trigger>
      }
    >
      <AddressForm index={index} close={close} />
    </Modal>
  );
}

function getKey<T extends string>(index: number, value: T) {
  return `stops.${index}.${value}` as const;
}

function AddressForm({ index, close }: { index: number; close: () => void }) {
  const { t } = useAppTranslation(['common', 'demand']);
  const { token } = useUserDetails();
  const lang = useLangProvider();
  const { toast } = useToast();

  const { watch, setValue } = useFormContext<IForm>();
  const country_id = watch('country_id');

  const form = useForm<IFavAddressList>({
    defaultValues: {
      address: watch(getKey(index, 'address')),
      city_id: Number(watch(getKey(index, 'city_id'))),
      description: watch(getKey(index, 'description')),
      name: '',
    },
  });
  const { handleSubmit, control } = form;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await axios_demand.post(`/users/addresses`, data, {
        headers: {
          ...(token && { Authorization: token }),
          'Accept-Language': lang,
        },
        params: {
          user_id: watch('user_id') || undefined,
        },
      });
      mutate('/users/addresses');
      toast({
        title: t('common:msg.successful_creation'),
        variant: 'success',
      });
      close();
      setValue(getKey(index, 'address'), data.address);
      setValue(getKey(index, 'city_id'), Number(data.city_id));
      setValue(getKey(index, 'description'), data.description);
    } catch (err) {
      const description =
        err instanceof AxiosError
          ? standardError(
              (err as AxiosError<DetailError>)?.response?.data ??
                (err as AxiosError)?.response ??
                err.message
            )
          : undefined;
      toast({
        title: t('common:msg.has_ocurred'),
        description,
        variant: 'danger',
      });
    }
  });
  return (
    <form
      onSubmit={(e) => {
        e.stopPropagation();
        onSubmit(e);
      }}
      className="flex flex-col items-stretch gap-4"
    >
      <h2 className="mx-auto text-xl font-semibold text-gray-800 dark:text-gray-200">
        {t('demand:addresses.addFavAddressForm.title')}
      </h2>
      <div
        className="bg-background-group dark:bg-background-group-dark flex flex-col gap-4 rounded
          p-4"
      >
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <WrappedInput isOnModal {...field} label={t('common:name')} />
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <WrappedInput
              isOnModal
              {...field}
              label={t('common:description')}
            />
          )}
        />
      </div>
      <div
        className="bg-background-group dark:bg-background-group-dark flex flex-col gap-4 rounded
          p-4"
      >
        <Controller
          control={control}
          name="address"
          rules={{ required: t('common:msg.required_field') }}
          render={({ field, fieldState: { error } }) => (
            <WrappedInput
              isOnModal
              {...field}
              label={t('common:address')}
              error={error?.message}
              requiredMark
            />
          )}
        />
        <CitySearch
          countryID={country_id}
          control={control}
          name="city_id"
          className="h-[42px] min-w-0 flex-1"
          innerClassName="h-[42px]"
          modal
          requiredMark
        />
      </div>

      <Map country_id={country_id} form={form} />

      <Button type="submit">{t('common:create')}</Button>
    </form>
  );
}

const mapClassName = 'h-64 w-screen max-w-md rounded';

/** TODO: Make a generic map that does this */
function Map({
  form,
  country_id,
}: {
  form: UseFormReturn<IFavAddressList>;
  country_id?: number;
}) {
  const { t } = useAppTranslation(['common', 'demand']);
  const { register, watch, setValue } = form;

  const [address, city_id, lat, lng] = watch([
    'address',
    'city_id',
    'latitude',
    'longitude',
  ]);

  const debouncedAddress = useDebounce(address, 1_000);

  const { data, error } = useFetch<IGeoDirection>({
    url: '/geo/direction',
    method: 'POST',
    data: { country_id, address: debouncedAddress, city_id },
  });

  useEffect(() => {
    if (data) {
      setValue('latitude', data.latitude);
      setValue('longitude', data.longitude);
    } else {
      setValue('latitude', '');
      setValue('longitude', '');
    }
  }, [debouncedAddress, city_id, data, setValue]);

  const onMoveEnd = useCallback(
    (c: LatLng) => {
      setValue('latitude', c.lat);
      setValue('longitude', c.lng);
    },
    [setValue]
  );

  const center = useMemo(() => {
    if (!lat || !lng) return;
    return { lat: Number(lat), lng: Number(lng) };
  }, [lat, lng]);

  return (
    <>
      {error && (
        <p
          {...register('error' as 'address', {
            required: true,
            shouldUnregister: true,
          })}
          tabIndex={0}
          className="max-w-md p-4 text-center font-semibold text-red-500 focus:text-base
            focus:font-bold focus:underline dark:text-red-300"
        >
          {t('demand:noAddress')}
        </p>
      )}
      {data ? (
        <SelectPoint
          key={`${center?.lat}${center?.lng}`}
          className={mapClassName}
          center={center}
          onMoveEnd={onMoveEnd}
        />
      ) : (
        <div className={clsx(mapClassName, 'flex items-center justify-center')}>
          <InlineSpinner className="m-auto size-8" />
          <div
            {...register('error' as 'address', {
              required: true,
              shouldUnregister: true,
            })}
            className="hidden"
          />
        </div>
      )}
    </>
  );
}
